class ProductUGC extends HTMLElement {
  constructor () {
    super()
    this.classPrefix = this.tagName.toLowerCase()
    this.carouselComponent = null
    this.shouldAutoplay = false
    this.desktopAutoplayIntervalMs = 5000
    this.autoplayTimerId = null
    this.isModalOpen = false
    this.userHovering = false
    this.currentAutoplayIndex = -1
    this.emblaSelectHandler = null
    this.onCarouselOpenHandler = null
    this.onAnyModalOpenHandler = null
    this.onModalHideHandler = null
    this.slideHoverHandlers = []
    this.autoplayMode = null
  }

  async connectedCallback () {
    this.autoplayCardVideos = this.dataset.autoplayCardVideos === 'true'
    this.updateAutoplayDecision()

    if (navigator.connection) {
      this.connectionChangeHandler = () => this.updateAutoplayDecision()
      navigator.connection.addEventListener('change', this.connectionChangeHandler)
    }

    this.onCarouselOpenHandler = () => { this.isModalOpen = true; this.stopAutoplay() }
    window.addEventListener('open:ugc-carousel', this.onCarouselOpenHandler)

    this.onAnyModalOpenHandler = () => { this.isModalOpen = true; this.stopAutoplay() }
    window.addEventListener('open:modal', this.onAnyModalOpenHandler)

    this.onModalHideHandler = () => {
      this.isModalOpen = false
      this.userHovering = false
      if (this.shouldAutoplay) setTimeout(() => this.setupAutoplayMode(), 150)
    }
    window.addEventListener('hide:page-obscure', this.onModalHideHandler)
    window.addEventListener('close:modal', this.onModalHideHandler)
    window.addEventListener('allow:page-scroll', this.onModalHideHandler)

    this.querySelectorAll('.ugc-card__content').forEach(overlay => {
      overlay.addEventListener('click', () => this.handleCardClick(overlay.closest('.ugc-card')))
      overlay.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          this.handleCardClick(overlay.closest('.ugc-card'))
        }
      })
    })

    this.querySelectorAll('.ugc-card a').forEach(link => {
      link.addEventListener('click', (e) => e.stopPropagation())
    })

    this.updateCountText()

    this.carouselComponent = this.querySelector('carousel-component')

    window.Tentbox?.observeLazyVideos?.(this)

    requestAnimationFrame(() => {
      if (this.shouldAutoplay && this.carouselComponent) {
        this.setupAutoplayMode()
      }
    })
  }

  updateAutoplayDecision () {
    const canPlayVideo = window.Tentbox?.canPlayVideo !== false
    this.shouldAutoplay = canPlayVideo && this.autoplayCardVideos
  }

  handleCardClick (clickedCard) {
    const allCards = Array.from(this.querySelectorAll('.ugc-card'))
    const clickedIndex = allCards.indexOf(clickedCard)

    const items = allCards.map(card => {
      const platform = card.dataset.platform || 'instagram'
      const username = (card.dataset.username || '').replace(/^@+/, '')
      const handle = username ? `@${username.toLowerCase()}` : ''
      let profileUrl = card.dataset.userProfileUrl || card.dataset.socialUrl || ''
      if (platform === 'instagram' && username) {
        profileUrl = `https://www.instagram.com/${username.toLowerCase()}`
      }

      return {
        videoUrl: card.dataset.videoUrl,
        imageUrl: card.dataset.thumbnailUrl,
        isVideo: true,
        altText: card.dataset.caption || 'User generated content',
        userName: card.dataset.username,
        user: {
          name: card.dataset.username || '',
          handle,
          handleLink: profileUrl,
          platform,
        },
        overlay: {
          show: false,
          title: null,
          copy: card.dataset.caption || null,
          link: card.dataset.socialUrl,
          product: null,
        },
      }
    })

    window.dispatchEvent(new CustomEvent('open:ugc-carousel', {
      detail: {
        index: clickedIndex >= 0 ? clickedIndex : 0,
        items,
      },
    }))
  }

  updateCountText () {
    const countElement = this.querySelector(`.${this.classPrefix}__heading-count`)
    const slideCount = this.querySelectorAll(`.${this.classPrefix}__carousel-slide`).length
    if (countElement && slideCount > 0) {
      countElement.textContent = countElement.textContent.replace('[num]', slideCount)
      countElement.classList.remove('hidden')
    }
  }

  disconnectedCallback () {
    if (this.connectionChangeHandler && navigator.connection) {
      navigator.connection.removeEventListener('change', this.connectionChangeHandler)
    }

    this.stopAutoplay()

    if (this.onCarouselOpenHandler) {
      window.removeEventListener('open:ugc-carousel', this.onCarouselOpenHandler)
    }
    if (this.onAnyModalOpenHandler) {
      window.removeEventListener('open:modal', this.onAnyModalOpenHandler)
    }
    if (this.onModalHideHandler) {
      window.removeEventListener('hide:page-obscure', this.onModalHideHandler)
      window.removeEventListener('close:modal', this.onModalHideHandler)
      window.removeEventListener('allow:page-scroll', this.onModalHideHandler)
    }

    const videos = this.querySelectorAll('video')
    videos.forEach(video => {
      video.pause()
      video.src = ''
    })
  }

  setupAutoplayMode () {
    if (!this.shouldAutoplay || this.isModalOpen) return
    if (!this.carouselComponent) return

    this.pauseAllVideos()

    const isDesktop = window.innerWidth >= 768
    const hasCarousel = !!(this.carouselComponent.embla)

    if (this.autoplayMode && this.autoplayMode === (isDesktop ? 'desktop' : 'mobile')) {
      return
    }

    this.stopAutoplay()

    if (isDesktop) {
      this.setupDesktopAutoplay(hasCarousel)
    } else {
      this.setupMobileAutoplay(hasCarousel)
    }

    this.autoplayMode = isDesktop ? 'desktop' : 'mobile'
  }

  setupDesktopAutoplay (hasCarousel = false) {
    this.attachHoverHandlers()

    if (hasCarousel && this.emblaSelectHandler && this.carouselComponent.embla && this.carouselComponent.embla.off) {
      this.carouselComponent.embla.off('select', this.emblaSelectHandler)
    }
    this.emblaSelectHandler = null

    const firstIndex = this.findNextVideoIndexFrom(-1)
    if (firstIndex === -1) return
    this.currentAutoplayIndex = firstIndex

    this.playVideoAtIndex(firstIndex, { ensureInView: hasCarousel })
    this.updateCardOpacities(firstIndex)
    this.scheduleNextDesktopStep(hasCarousel)
  }

  setupMobileAutoplay (hasCarousel = false) {
    this.detachHoverHandlers()

    if (hasCarousel) {
      const embla = this.carouselComponent.embla
      const handleSelect = () => {
        if (this.isModalOpen || !this.shouldAutoplay) return
        const index = embla.selectedScrollSnap()
        this.currentAutoplayIndex = index
        this.pauseAllVideos()
        this.playVideoAtIndex(index, { ensureInView: false })
        this.updateCardOpacities(null)
      }

      this.emblaSelectHandler = handleSelect
      embla.on('select', handleSelect)
      handleSelect()
    } else {
      this.pauseAllVideos()
      this.updateCardOpacities(null)
    }
  }

  scheduleNextDesktopStep (hasCarousel = false) {
    if (this.autoplayTimerId) clearTimeout(this.autoplayTimerId)
    this.autoplayTimerId = setTimeout(() => {
      if (this.isModalOpen || this.userHovering || !this.carouselComponent) return
      const nextIndex = this.findNextVideoIndexFrom(this.currentAutoplayIndex)
      if (nextIndex === -1) return
      this.currentAutoplayIndex = nextIndex
      this.playVideoAtIndex(nextIndex, { ensureInView: hasCarousel })
      this.scheduleNextDesktopStep(hasCarousel)
    }, this.desktopAutoplayIntervalMs)
  }

  playVideoAtIndex (index, { ensureInView } = { ensureInView: false }) {
    const slides = this.getSlides()
    const slide = slides[index]
    if (!slide) return
    const video = slide.querySelector('video')
    if (!video) return

    if (ensureInView && this.carouselComponent && this.carouselComponent.embla) {
      const inView = this.isSlideFullyVisible(index)
      if (!inView) {
        this.carouselComponent.dispatchEvent(new CustomEvent('carousel:scrollTo', { detail: { index, jump: true } }))
      }
    }

    this.pauseAllVideos()
    window.Tentbox?.ensureLazyVideoSource?.(video)
    try {
      video.play().catch(() => {})
    } catch (e) {}

    this.updateCardOpacities(index)
  }

  getViewportElement () {
    if (!this.carouselComponent) return null
    return this.carouselComponent.querySelector('[carousel]')
  }

  isSlideFullyVisible (index) {
    const viewport = this.getViewportElement()
    const slides = this.getSlides()
    const slide = slides[index]
    if (!viewport || !slide) return false

    const viewportRect = viewport.getBoundingClientRect()
    const slideRect = slide.getBoundingClientRect()

    const tolerance = 0.5
    const fullyVisible = (slideRect.left >= viewportRect.left - tolerance) && (slideRect.right <= viewportRect.right + tolerance)
    return fullyVisible
  }

  pauseAllVideos () {
    const videos = this.querySelectorAll('video')
    videos.forEach(v => {
      try { v.pause() } catch (e) {}
    })
  }

  getSlides () {
    return this.querySelectorAll(`.${this.classPrefix}__carousel-slide`)
  }

  findNextVideoIndexFrom (startIndex) {
    const slides = Array.from(this.getSlides())
    if (slides.length === 0) return -1
    const total = slides.length
    let i = (startIndex + 1 + total) % total
    for (let count = 0; count < total; count++) {
      const hasVideo = !!slides[i].querySelector('video')
      if (hasVideo) return i
      i = (i + 1) % total
    }
    return -1
  }

  attachHoverHandlers () {
    const slides = Array.from(this.getSlides())
    if (this.slideHoverHandlers.length > 0) return

    slides.forEach((slide, index) => {
      const onEnter = () => {
        if (this.isModalOpen) return
        this.userHovering = true
        if (!this.shouldAutoplay) return
        this.pauseAllVideos()
        this.currentAutoplayIndex = index
        this.playVideoAtIndex(index, { ensureInView: false })
        this.updateCardOpacities(index)
        if (this.autoplayTimerId) clearTimeout(this.autoplayTimerId)
      }
      const onLeave = () => {
        if (this.isModalOpen) return
        this.userHovering = false
        if (!this.shouldAutoplay) return
        this.scheduleNextDesktopStep()
      }
      slide.addEventListener('mouseenter', onEnter)
      slide.addEventListener('mouseleave', onLeave)
      this.slideHoverHandlers.push({ slide, onEnter, onLeave })
    })
  }

  detachHoverHandlers () {
    if (this.slideHoverHandlers.length === 0) return
    this.slideHoverHandlers.forEach(({ slide, onEnter, onLeave }) => {
      slide.removeEventListener('mouseenter', onEnter)
      slide.removeEventListener('mouseleave', onLeave)
    })
    this.slideHoverHandlers = []
  }

  stopAutoplay () {
    if (this.autoplayTimerId) {
      clearTimeout(this.autoplayTimerId)
      this.autoplayTimerId = null
    }
    this.detachHoverHandlers()
    const embla = this.carouselComponent && this.carouselComponent.embla
    if (embla && this.emblaSelectHandler && typeof embla.off === 'function') {
      embla.off('select', this.emblaSelectHandler)
    }
    this.emblaSelectHandler = null
    this.pauseAllVideos()
    this.autoplayMode = null
  }

  updateCardOpacities (activeIndex) {
    const isDesktop = window.innerWidth >= 768
    const slides = Array.from(this.getSlides())
    slides.forEach((slide, index) => {
      const card = slide.querySelector('.ugc-card')
      if (!card) return
      if (isDesktop) {
        const isActive = typeof activeIndex === 'number' && index === activeIndex
        card.style.filter = isActive ? '' : 'brightness(0.85)'
      } else {
        card.style.filter = ''
      }
    })
  }
}

if (!customElements.get('product-ugc')) {
  customElements.define('product-ugc', ProductUGC)
}
if (!customElements.get('product-ugc-api')) {
  customElements.define('product-ugc-api', ProductUGC)
}
